import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useId, type ReactNode } from "react";

import { CHANNEL_CELL_SX } from "./preferenceProvenance";

/**
 * One collapsible section of a notification settings tab: a gray header band with the
 * section's name, a short status beside it and a caret, over the section's rows.
 */

export type SectionAccordionProps = {
  label: string;
  /** Beside the name in the header band: a {@link SectionHeaderStatus}, a marker, or nothing. */
  status?: ReactNode;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** The grid the section's rows are drawn on. Its last column is the caret lane. */
  gridTemplateColumns: string;
  /** The columns between the name and the caret lane, left empty in the header. */
  controlColumns: number;
  children: ReactNode;
};

/** A short status beside a section's name, such as "Off". */
export function SectionHeaderStatus({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {children}
    </Typography>
  );
}

type SectionHeaderRowProps = Omit<SectionAccordionProps, "children"> & {
  summaryId: string;
  detailsId: string;
};

/**
 * The section's heading band: its name, its status and the caret.
 *
 * It stays the Accordion's FIRST child. MUI treats that child as the header and
 * exposes the expanded state through context, which is what lets `AccordionSummary`
 * live inside this component rather than directly under the accordion.
 */
function SectionHeaderRow({
  label,
  status,
  summaryId,
  detailsId,
  expanded,
  onToggleExpanded,
  gridTemplateColumns,
  controlColumns,
}: SectionHeaderRowProps) {
  return (
    <>
      {/*
        Drawn on the rows' grid so the caret lands in the caret lane rather than over
        a control column.

        The caret is a SIBLING of the summary, never a descendant: a button inside
        the summary's own `<button>` is invalid HTML and undefined ARIA.
      */}
      {/*
        The whole band toggles, not just the name: a click on its padding or the empty
        control lanes lands on the band itself. The summary and the caret handle their
        own clicks, so those are not counted twice.
      */}
      <Box
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            onToggleExpanded();
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns,
          alignItems: "center",
          columnGap: 1,
          px: 3,
          cursor: "pointer",
          // The gray band is the section divider. It reads as a heading without
          // the ruled box the outlined variant drew, which is what made the
          // sections hard to tell apart from the rows inside them.
          backgroundColor: (theme) => theme.palette.surfaceSubtle,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/*
          The summary owns the name column and draws no caret of its own, because its
          built-in `expandIcon` renders at that column's right edge rather than in
          the caret lane.
        */}
        <AccordionSummary
          id={summaryId}
          aria-controls={detailsId}
          sx={{ minWidth: 0, px: 0, backgroundColor: "transparent" }}
        >
          {/*
            One line height across the name and its status, rather than each font's
            own. `overline` sets 1.6 and `body2` 1.43, so centring two different line
            boxes left the status sitting a pixel or two off the name's optical centre.
          */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.primary"
              sx={{ fontSize: "0.8125rem", fontWeight: 700, lineHeight: 1.5 }}
            >
              {label}
            </Typography>
            {status}
          </Stack>
        </AccordionSummary>

        {/* Reserved though nothing draws in them: the grid is shared with the rows. */}
        {Array.from({ length: controlColumns }, (_, column) => (
          <Box key={column} sx={CHANNEL_CELL_SX} />
        ))}

        {/* The caret, in its own lane past the control columns. */}
        <Box sx={CHANNEL_CELL_SX}>
          <IconButton
            size="small"
            aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={() => onToggleExpanded()}
          >
            <ExpandMore
              sx={{
                transform: expanded ? "rotate(180deg)" : "none",
                transition: (theme) => theme.transitions.create("transform"),
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </>
  );
}

export function SectionAccordion({ children, ...header }: SectionAccordionProps) {
  const summaryId = useId();
  const detailsId = useId();

  return (
    /*
      A band inside the tab's widget rather than a widget of its own. Five floating
      papers said nothing about what they collectively were, which is why it was hard
      to tell which section a given row belonged to.

      Space below an expanded section rather than above every one. Collapsed
      sections read as a list and want to sit close together; an open one needs its
      rows separated from the next header.
    */
    <Box sx={{ mb: header.expanded ? 3 : 0 }}>
      <Accordion
        /*
        No outline and no shadow: the widget is the container, so an outlined
        accordion inside it drew a second box around the same content. What
        separates one section from the next is the gray header band, which is
        quieter and matches how the rest of the app groups rows.
      */
        disableGutters
        elevation={0}
        expanded={header.expanded}
        onChange={header.onToggleExpanded}
        sx={{ "&::before": { display: "none" }, backgroundColor: "transparent" }}
        /*
        MUI wires the collapsible region from the *first child's* props, which is
        the header row below rather than the summary button. Naming the region from
        the button instead keeps its accessible name to the section's own heading.
      */
        slotProps={{ region: { id: detailsId, "aria-labelledby": summaryId } }}
      >
        <SectionHeaderRow {...header} summaryId={summaryId} detailsId={detailsId} />
        <AccordionDetails sx={{ px: 3, pt: 0.5, pb: 1 }}>{children}</AccordionDetails>
      </Accordion>
    </Box>
  );
}
